import { useState, useCallback } from 'react';
import mallaService from '../../../../services/mallaService';
import { showToast } from '../../../../utils/showToast';

export default function useMallaLogic() {
  const [malla, setMalla] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedCursoId, setSelectedCursoId] = useState(null);
  const [selectedCursoNombre, setSelectedCursoNombre] = useState(null);

  // Modal state
  const [modal, setModal] = useState(null); // 'nuevo', 'eliminar', null
  const [mallaSeleccionada, setMallaSeleccionada] = useState(null);

  // Form state para nuevo
  const [formNuevo, setFormNuevo] = useState({
    materia_id: '',
    docente_id: '',
    horas_malla: 0,
    orden: '',
    modalidad: 'presencial',
    activo: true,
  });

  const [savingNuevo, setSavingNuevo] = useState(false);
  const [savingCambios, setSavingCambios] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Cerrar modal
  const cerrarModal = useCallback(() => {
    setModal(null);
    setMallaSeleccionada(null);
    setFormNuevo({
      materia_id: '',
      docente_id: '',
      horas_malla: 0,
      orden: '',
      modalidad: 'presencial',
      activo: true,
    });
  }, []);

  // Fetch malla del curso
  const fetchMalla = useCallback(async (cursoId) => {
    if (!cursoId) {
      setMalla([]);
      return;
    }

    setLoading(true);
    setListError(null);

    try {
      const response = await mallaService.obtenerMallaPorCurso(cursoId);
      setMalla(response.materias || []);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('❌ Error al obtener malla:', error);
      setListError(error.message || 'Error al obtener la malla del curso');
      setMalla([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Manejar cambio de curso
  const handleCursoChange = useCallback((cursoId, cursoNombre) => {
    setSelectedCursoId(cursoId);
    setSelectedCursoNombre(cursoNombre);
    cerrarModal();
    if (cursoId) {
      fetchMalla(cursoId);
    } else {
      setMalla([]);
    }
  }, [cerrarModal, fetchMalla]);

  // Abrir modales
  const abrirModalNuevo = useCallback(() => {
    if (!selectedCursoId) {
      showToast({ type: 'warning', message: 'Selecciona un curso primero' });
      return;
    }
    setFormNuevo({
      materia_id: '',
      docente_id: '',
      horas_malla: 0,
      orden: '',
      modalidad: 'presencial',
      activo: true,
    });
    setModal('nuevo');
  }, [selectedCursoId]);

  const abrirModalEliminar = useCallback((item) => {
    setMallaSeleccionada(item);
    setModal('eliminar');
  }, []);

  // Crear materia en malla
  const handleAgregarMateria = useCallback(async () => {
    if (!formNuevo.materia_id) {
      showToast({ type: 'danger', message: 'Selecciona una materia' });
      return;
    }

    setSavingNuevo(true);

    try {
      await mallaService.agregarMateriaCurso({
        curso_id: selectedCursoId,
        materia_id: parseInt(formNuevo.materia_id, 10),
        docente_id: formNuevo.docente_id ? parseInt(formNuevo.docente_id, 10) : null,
        horas_malla: parseFloat(formNuevo.horas_malla) || 0,
        orden: formNuevo.orden ? parseInt(formNuevo.orden, 10) : undefined,
        modalidad: formNuevo.modalidad || 'presencial',
        activo: formNuevo.activo,
      });

      showToast({ type: 'success', message: 'Materia agregada a la malla' });
      cerrarModal();
      await fetchMalla(selectedCursoId);
    } catch (error) {
      console.error('Error al agregar materia:', error);
      const message = error.message || 'Error al agregar materia';
      const normalized = message.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const isDuplicate = error.status === 409 || normalized.includes('ya esta');
      showToast({
        type: isDuplicate ? 'warning' : 'danger',
        message,
      });
    } finally {
      setSavingNuevo(false);
    }
  }, [formNuevo, selectedCursoId, cerrarModal, fetchMalla]);

  /*
  // Actualizar malla
  const handleActualizarMalla = useCallback(async () => {
    if (!mallaSeleccionada) return;

    setSavingEditar(true);

    try {
      await mallaService.actualizarMalla(mallaSeleccionada.id, {
        docente_id: formEditar.docente_id ? parseInt(formEditar.docente_id) : null,
        horas_malla: parseFloat(formEditar.horas_malla) || 0,
        orden: formEditar.orden ? parseInt(formEditar.orden) : undefined,
        modalidad: formEditar.modalidad || 'presencial',
        activo: formEditar.activo,
      });

      showToast({ type: 'success', message: 'Malla actualizada correctamente' });
      cerrarModal();
      await fetchMalla(selectedCursoId);
    } catch (error) {
      console.error('❌ Error al actualizar malla:', error);
      showToast({
        type: 'danger',
        message: error.message || 'Error al actualizar malla',
      });
    } finally {
      setSavingEditar(false);
    }
  }, [formEditar, mallaSeleccionada, selectedCursoId, cerrarModal, fetchMalla]);

  */

  // Guardar cambios en malla (edicion en tabla)
  const guardarCambiosMalla = useCallback(async (changes = []) => {
    if (!selectedCursoId) {
      showToast({ type: 'warning', message: 'Selecciona un curso primero' });
      return false;
    }
    if (!Array.isArray(changes) || changes.length === 0) {
      showToast({ type: 'info', message: 'No hay cambios para guardar.' });
      return false;
    }

    for (const change of changes) {
      const horasRaw = change?.horas_malla;
      const horasValue =
        horasRaw === '' || horasRaw === null || horasRaw === undefined
          ? 0
          : parseFloat(horasRaw);
      if (Number.isNaN(horasValue) || horasValue < 0 || horasValue > 999.99) {
        showToast({
          type: 'warning',
          message: 'Las horas de malla deben estar entre 0 y 999.99.',
        });
        return false;
      }
    }

    setSavingCambios(true);

    try {
      await Promise.all(
        changes.map((change) => {
          const horasRaw = change?.horas_malla;
          const horasValue =
            horasRaw === '' || horasRaw === null || horasRaw === undefined
              ? 0
              : parseFloat(horasRaw);
          const modalidadValue = (change?.modalidad || 'presencial')
            .toString()
            .trim()
            .toLowerCase();
          const activoValue = Boolean(change?.activo);

          // docente_id ya no se edita desde esta tabla (ver ModalDocentesCalificados
          // en MallaList) — se omite del payload a proposito para no pisar el
          // valor historico que ya tenga la fila.
          return mallaService.actualizarMalla(change.id, {
            horas_malla: horasValue,
            modalidad: modalidadValue,
            activo: activoValue,
          });
        })
      );

      showToast({ type: 'success', message: 'Malla actualizada correctamente' });
      await fetchMalla(selectedCursoId);
      return true;
    } catch (error) {
      console.error('ƒ?O Error al actualizar malla:', error);
      showToast({
        type: 'danger',
        message: error.message || 'Error al actualizar malla',
      });
      return false;
    } finally {
      setSavingCambios(false);
    }
  }, [fetchMalla, selectedCursoId]);

  // Eliminar materia de malla
  const handleEliminarMateria = useCallback(async () => {
    if (!mallaSeleccionada) return;

    setDeleting(true);

    try {
      await mallaService.eliminarMateriaCurso(mallaSeleccionada.id);

      showToast({ type: 'success', message: 'Materia eliminada de la malla' });
      cerrarModal();
      await fetchMalla(selectedCursoId);
    } catch (error) {
      console.error('❌ Error al eliminar materia:', error);
      showToast({
        type: 'danger',
        message: error.message || 'Error al eliminar materia',
      });
    } finally {
      setDeleting(false);
    }
  }, [mallaSeleccionada, selectedCursoId, cerrarModal, fetchMalla]);

  // Resumen malla
  const resumen = {
    total: malla.length,
    horasTotales: malla.reduce((sum, m) => sum + (parseFloat(m.horas_malla) || 0), 0),
  };

  return {
    // State
    malla,
    loading,
    listError,
    lastUpdated,
    selectedCursoId,
    selectedCursoNombre,
    resumen,
    modal,
    mallaSeleccionada,
    formNuevo,
    setFormNuevo,
    savingNuevo,
    savingCambios,
    deleting,

    // Handlers
    handleCursoChange,
    fetchMalla,
    abrirModalNuevo,
    abrirModalEliminar,
    cerrarModal,
    handleAgregarMateria,
    guardarCambiosMalla,
    handleEliminarMateria,
  };
}

